import { ColumnVisibilityItem } from "providers/Auth";
import { mergeSavedColumns } from "shared/utils/column-order";
import {
  LEADS_COLUMNS_TAB_KEY,
  LEADS_VIEW_ORDERED_COLUMNS_KEY,
  LeadColumnId
} from "../constants";
import type { LeadSavedView } from "../types";

const COLUMNS_HIDDEN_FOR_EXISTING_VIEWS = new Set<string>([
  LeadColumnId.PrescreeningResult
]);

/**
 * Merge a saved Leads column list (the user's own, or a saved view's) with the table's defaults:
 * order and shown/hidden come from the saved list, a column it predates is added where
 * mergeSavedColumns puts new columns.
 */
export const mergeColumnsWithDefaults = (
  savedColumns: ColumnVisibilityItem[] | undefined,
  defaults: ColumnVisibilityItem[]
) => mergeSavedColumns(savedColumns, defaults);

// A view saved before columns could be reordered: its shown/hidden columns are kept, in Boom's
// standard order. Prescreening result predates no view the user meant to show it in, so it
// starts hidden there.
const mergeLegacyViewColumns = (
  savedColumns: ColumnVisibilityItem[] | undefined,
  defaults: ColumnVisibilityItem[]
) => {
  if (!savedColumns?.length) return defaults;
  const checkedById = new Map(
    savedColumns.map((column) => [column.id, column.checked])
  );
  return defaults.map((column) => {
    const checked = checkedById.get(column.id);
    if (checked !== undefined)
      return { ...column, checked: column.always_on ? true : checked };
    return COLUMNS_HIDDEN_FOR_EXISTING_VIEWS.has(column.id)
      ? { ...column, checked: false }
      : column;
  });
};

/** The columns a saved view shows, merged with the table's current defaults. */
export const getViewColumns = (
  view: Pick<LeadSavedView, "columns">,
  defaults: ColumnVisibilityItem[]
): ColumnVisibilityItem[] => {
  const ordered = view.columns?.[LEADS_VIEW_ORDERED_COLUMNS_KEY];
  if (ordered?.length) return mergeSavedColumns(ordered, defaults);
  return mergeLegacyViewColumns(
    view.columns?.[LEADS_COLUMNS_TAB_KEY],
    defaults
  );
};
